import React, { useContext } from "react";
import { CSVLink } from "react-csv";
import { Button, ButtonGroup } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import ReactToPrint from "react-to-print";
import copy from "copy-to-clipboard";
import { showInfoNotification } from "components/framework/notification/NotificationUtil";
import { utils, writeFile } from "xlsx";
import { AppContext } from "services/appContext/AppContext";

export default function ExportButtons(props) {
  const intl = useIntl();
  const table = dataToTable(props.data, props.columns, props.hasExpandedData);
  const { appContext } = useContext(AppContext);
  const theme = appContext.theme;

  const handleBeforePrint = () => {
    if (props.printRef?.current) {
      const allElements = props.printRef.current.querySelectorAll("*");
      allElements.forEach((el) => {
        const computed = window.getComputedStyle(el);
        if (
          computed.overflow === "auto" ||
          computed.overflow === "hidden" ||
          computed.overflowX === "auto" ||
          computed.overflowX === "hidden" ||
          computed.overflowX === "scroll"
        ) {
          el.setAttribute("data-print-overflow", el.style.overflow);
          el.setAttribute("data-print-overflowX", el.style.overflowX);
          el.style.overflow = "visible";
          el.style.overflowX = "visible";
          el.style.width = "max-content";
        }
      });
    }
    if (props.onBeforePrint) return props.onBeforePrint();
  };  

  const handleAfterPrint = () => {
    if (props.printRef?.current) {
      const allElements = props.printRef.current.querySelectorAll("*");
      allElements.forEach((el) => {
        if (el.hasAttribute("data-print-overflow")) {
          el.style.overflow = el.getAttribute("data-print-overflow");
          el.style.overflowX = el.getAttribute("data-print-overflowX");
          el.style.width = "";
          el.removeAttribute("data-print-overflow");
          el.removeAttribute("data-print-overflowX");
        }
      });
    }
    if (props.onAfterPrint) props.onAfterPrint();
  };

  return (
    <ButtonGroup>
      { !props.isInventory &&
      <>
        <CSVLink
          data={table}
          target="_blank"
          className={`btn btn-default btn-sm text-white rounded mr-2 border-0 ${theme === "light" ? "bg-lblue" : "bg-primary"}`}
          filename={"result.csv"}>
          <FormattedMessage id="table.exportToCsv" />
        </CSVLink>
        <Button color="default" type="button" size="sm" className={`text-white rounded mr-2 border-0 ${theme === "light" ? "bg-lblue" : "bg-primary"}`} onClick={() => exportToXlsxHandler(table)}>
          <FormattedMessage id="table.exportToXlsx" />
        </Button>
      </>
      }
      {props.isInventory &&
      <>
        <Button color="default" type="button" size="sm" className={`text-white rounded mr-2 border-0 ${theme === "light" ? "bg-lblue" : "bg-primary"}`} onClick={() => props.onExportCsvButtonClick()}>
          <FormattedMessage id="table.exportToCsv" />
        </Button>
        <Button color="default" type="button" size="sm" className={`text-white rounded mr-2 border-0 ${theme === "light" ? "bg-lblue" : "bg-primary"}`} onClick={() => props.onExportXlsxButtonClick()}>
          <FormattedMessage id="table.exportToXlsx" />
        </Button>
      </>
      }
      {props.showCopy &&
        <Button color="default" type="button" size="sm" className={`text-white rounded mr-2 border-0 ${theme === "light" ? "bg-lblue" : "bg-primary"}`} onClick={() => copyHandler(table, intl)}>
          <FormattedMessage id="table.copy" />
        </Button>}
      {props.showPrint &&
        <ReactToPrint
          onBeforeGetContent={handleBeforePrint}
          onAfterPrint={handleAfterPrint}
          pageStyle={`
            @page { size: landscape; margin: 5mm; }
            @media print {
              * { overflow: visible !important; }
              table { 
                width: 100% !important; 
                table-layout: auto !important; 
              }
              th, td { 
                font-size: 7px !important; 
                padding: 1px 3px !important; 
                white-space: nowrap !important; 
              }
              input[type="checkbox"],
              input[type="radio"],
              label,
              .toggle,
              .custom-control,
              .custom-switch {
                width: 16px !important;
                height: 16px !important;
                min-width: unset !important;
              }
            }
          `}
          trigger={() => (
            <Button color="default" type="button" size="sm" className={`text-white rounded border-0 ${theme === "light" ? "bg-lblue" : "bg-primary"}`} >
              <FormattedMessage id="table.print" />
            </Button>
          )}
          content={() => props.printRef.current}
        />}
    </ButtonGroup>
  );
}

export function exportToXlsxHandler(matrix) {
  let workbook = { SheetNames: ["Sheet1"], Sheets: { Sheet1: utils.aoa_to_sheet(matrix) } };

  writeFile(workbook, "result.xlsx");
}

function copyHandler(matrix, intl) {
  let string = matrix.map((x) => x.join(", ")).join("\n");

  copy(string);

  showInfoNotification(intl.formatMessage({ id: "table.copyNotification" }));
}

function dataToTable(data, columns, hasExpandedData) {
  let table = [columns.map((column) => column.Header)];

  const rowsData = [];
  if (data) {
    data.forEach((row) => {
      rowsData.push(columns.map((column) => {
        // Handle both string and function accessors
        let value;
        if (typeof column.accessor === 'function') {
          value = column.accessor(row);
        } else {
          value = row[column.accessor];
        }
        return value ? `${value}` : "";
      }));
      
      if (hasExpandedData && row.subRows && row.subRows.length > 0) {
        row.subRows.forEach(subRow => {
          rowsData.push(columns.map((column) => {
            // Handle both string and function accessors for subRows too
            let value;
            if (typeof column.accessor === 'function') {
              value = column.accessor(subRow);
            } else {
              value = subRow[column.accessor];
            }
            return value ? `${value}` : "";
          }));
        });
      }
    });
  }

  rowsData.forEach((x) => table.push(x));
  return table;
}
